import React from 'react';
import { Box, Button, IconButton, TextField, Tooltip } from '@mui/material';
import {
  MaterialReactTable,
  // createRow,
  useMaterialReactTable,
} from 'material-react-table';
import { lazy, useMemo, useState } from 'react';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import DeleteIcon from '@mui/icons-material/Delete';
import moment from 'moment';
import toast from 'react-hot-toast';
import { useQuery } from 'react-query';
import { axiosInstance } from '../../../AxiosInstance';
import { apiPath } from '../../../../../config';
import { dateFormatForServer } from '../../../../../utils/dateUtils';
import { MvaPriceUtils } from '../../../../../utils/mvaPriceUtils';
import { queryKeys } from '../../../../../utils/queryKeys';
import { useCreateNewPortionSizePrice } from './price_form_table/usePortionSizePriceApi';
import pricingCategoryEnum from './price_form_table/pricingCategoryEnum';
import {
  useCreateNewPortion,
  useDeletePortion,
  useGetAllPortionInfo,
  useSetPortionAsDefault,
} from './usePortionApi';
const PriceHistoryFormDialog = lazy(() =>
  import('./price_form_table/PriceHistoryFormDialog')
);

const getApplicablePortionPriceHistory = (priceList = []) => {
  const portionSizePrices = priceList.filter(
    (price) => price?.category === pricingCategoryEnum.PortionSizePrice
  );

  if (portionSizePrices.length > 0) {
    return portionSizePrices;
  }

  return priceList.filter((price) => !price?.category);
};

const getCurrentApplicablePrice = (priceList = []) => {
  const sortedPriceList = [...getApplicablePortionPriceHistory(priceList)]
    .filter((price) => price?.startDate)
    .sort((firstPrice, secondPrice) => {
      return Date.parse(secondPrice.startDate) - Date.parse(firstPrice.startDate);
    });

  if (sortedPriceList.length === 0) {
    return null;
  }

  const currentApplicablePrice = sortedPriceList.find((price) =>
    !moment(Date.parse(price.startDate)).isAfter(moment(), 'day')
  );

  return currentApplicablePrice ?? sortedPriceList[sortedPriceList.length - 1];
};

const getApplicablePriceDraft = (priceInfo = {}) => ({
  priceExcludingVat: MvaPriceUtils.formatEditablePrice(
    MvaPriceUtils.getAmountExcludingMva(priceInfo)
  ),
  priceIncludingVat: MvaPriceUtils.formatEditablePrice(
    MvaPriceUtils.getAmountIncludingMva(priceInfo)
  ),
});

const buildInitialPortionPricePayload = (values) => {
  const { amount, vatAmount } = MvaPriceUtils.getAmountSplit(values);

  return {
    startDate: moment().add(1, 'day').format(dateFormatForServer),
    amount,
    vatAmount,
    employeeContributionAmount: amount,
    employeeContributionVatAmount: vatAmount,
  };
};

const shouldCreateInitialPortionPrice = (priceInfo) =>
  priceInfo?.amount !== null && priceInfo?.amount > 0;

const PortionFormTable = ({ mealId, showApplicablePrices = false }) => {
  // console.log('mealId', mealId);

  const [validationErrors, setValidationErrors] = useState({});
  const [applicablePriceDraft, setApplicablePriceDraft] = useState(null);

  //call READ hook
  const {
    data: fetchedMealPortions = { data: [] },
    isError: isLoadingUsersError,
    isFetching: isFetchingMealPortions,
    isLoading: isLoadingMealPortions,
  } = useGetAllPortionInfo(mealId);

  const portionIds = useMemo(
    () => fetchedMealPortions.data?.map((portion) => portion.id).filter(Boolean) ?? [],
    [fetchedMealPortions.data]
  );

  const { data: portionPriceHistoryById = {}, isFetching: isFetchingApplicablePrices } =
    useQuery({
      queryKey: [queryKeys.getAllPortionSizePrice, 'portion-table', mealId, portionIds.join(',')],
      queryFn: async () => {
        const portionPriceHistory = await Promise.all(
          portionIds.map(async (portionId) => {
            try {
              const response = await axiosInstance.get(
                `${apiPath.portionSize}/${portionId}/prices`
              );

              return [portionId, response?.data?.data ?? []];
            } catch (err) {
              console.log('get-all-portion-size-price', err.message);
              return [portionId, []];
            }
          })
        );

        return Object.fromEntries(portionPriceHistory);
      },
      enabled: showApplicablePrices && portionIds.length > 0,
      refetchOnWindowFocus: false,
    });

  const currentApplicablePricesById = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(portionPriceHistoryById).map(([portionId, priceList]) => [
          portionId,
          getCurrentApplicablePrice(priceList),
        ])
      ),
    [portionPriceHistoryById]
  );

  const getDisplayedPriceInfo = (row) =>
    currentApplicablePricesById[row.original.id] ?? row.original;

  const ensureApplicablePriceValuesOnRow = (row, draftValues) => {
    row._valuesCache.priceExcludingVat = draftValues.priceExcludingVat;
    row._valuesCache.priceIncludingVat = draftValues.priceIncludingVat;
  };

  const updateApplicablePriceDraft = (row, fieldName, value) => {
    const currentDraft =
      applicablePriceDraft ?? getApplicablePriceDraft(getDisplayedPriceInfo(row));
    const numericValue = MvaPriceUtils.getNumericValue(value);

    let nextDraft;

    if (numericValue === null) {
      nextDraft = {
        ...currentDraft,
        priceExcludingVat: '',
        priceIncludingVat: '',
      };
    } else if (fieldName === 'priceExcludingVat') {
      nextDraft = {
        ...currentDraft,
        priceExcludingVat: value,
        priceIncludingVat: MvaPriceUtils.formatEditablePrice(
          MvaPriceUtils.getAmountIncludingMva({ priceExcludingVat: numericValue })
        ),
      };
    } else {
      nextDraft = {
        ...currentDraft,
        priceIncludingVat: value,
        priceExcludingVat: MvaPriceUtils.formatEditablePrice(
          MvaPriceUtils.getAmountExcludingMva({ amountIncludingMva: numericValue })
        ),
      };
    }

    ensureApplicablePriceValuesOnRow(row, nextDraft);
    setApplicablePriceDraft(nextDraft);
  };

  const columns = useMemo(() => {
    const baseColumns = [
      {
        accessorKey: 'id',
        header: 'Id',
        enableEditing: false,
      },
      {
        accessorKey: 'size',
        header: 'Portion size',
        enableEditing: true,
        size: 80,
        muiEditTextFieldProps: {
          required: true,
          variant: 'outlined',
          error: !!validationErrors?.size,
          helperText: validationErrors?.size,
          //remove any previous validation errors when user focuses on the input
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              size: undefined,
            }),
        },
      },
      {
        accessorKey: 'minimumQuantity',
        header: 'Quantity(Min.)',
        enableSorting: false,
        muiEditTextFieldProps: {
          required: true,
          variant: 'outlined',
          error: !!validationErrors?.minimumQuantity,
          helperText: validationErrors?.minimumQuantity,
          type: 'number',
          //remove any previous validation errors when user focuses on the input
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              minimumQuantity: undefined,
            }),
        },
      },
      {
        accessorKey: 'maximumQuantity',
        header: 'Quantity(Max.)',
        enableSorting: false,
        muiEditTextFieldProps: {
          required: true,
          variant: 'outlined',
          error: !!validationErrors?.maximumQuantity,
          helperText: validationErrors?.maximumQuantity,
          type: 'number',
          //remove any previous validation errors when user focuses on the input
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              maximumQuantity: undefined,
            }),
        },
      },
      {
        accessorKey: 'priceExcludingVat',
        header: showApplicablePrices
          ? 'Meal Price (Excl. MVA)'
          : 'Price per unit(Excl. MVA)',
        enableSorting: false,
        Cell: ({ row }) => (
          <>
            {MvaPriceUtils.formatPrice(
              MvaPriceUtils.getAmountExcludingMva(getDisplayedPriceInfo(row))
            )}
          </>
        ),
        muiEditTextFieldProps: {
          required: true,
          variant: 'outlined',

          error: !!validationErrors?.priceExcludingVat,
          helperText: validationErrors?.priceExcludingVat,
          type: 'number',
          //remove any previous validation errors when user focuses on the input
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              priceExcludingVat: undefined,
            }),
        },
        ...(showApplicablePrices
          ? {
              Edit: ({ row }) => {
                const draftValues =
                  applicablePriceDraft ??
                  getApplicablePriceDraft(getDisplayedPriceInfo(row));
                ensureApplicablePriceValuesOnRow(row, draftValues);

                return (
                  <TextField
                    required
                    fullWidth
                    type='number'
                    size='small'
                    margin='none'
                    variant='outlined'
                    value={draftValues.priceExcludingVat}
                    error={!!validationErrors?.priceExcludingVat}
                    helperText={validationErrors?.priceExcludingVat}
                    onFocus={() =>
                      setValidationErrors({
                        ...validationErrors,
                        priceExcludingVat: undefined,
                      })
                    }
                    onChange={(event) =>
                      updateApplicablePriceDraft(
                        row,
                        'priceExcludingVat',
                        event.target.value
                      )
                    }
                  />
                );
              },
            }
          : {}),
      },
    ];

    if (showApplicablePrices) {
      baseColumns.push(
        {
          id: 'mvaAmount',
          header: 'MVA',
          enableEditing: true,
          enableSorting: false,
          Cell: ({ row }) => (
            <>
              {MvaPriceUtils.formatPrice(
                MvaPriceUtils.getMvaAmount(getDisplayedPriceInfo(row))
              )}
            </>
          ),
          Edit: ({ row }) => {
            const draftValues =
              applicablePriceDraft ??
              getApplicablePriceDraft(getDisplayedPriceInfo(row));
            ensureApplicablePriceValuesOnRow(row, draftValues);

            return (
              <Box sx={{ width: '100%', py: 1.5 }}>
                {MvaPriceUtils.formatPrice(
                  MvaPriceUtils.getMvaAmount({
                    priceExcludingVat: draftValues.priceExcludingVat,
                  })
                )}
              </Box>
            );
          },
        },
        {
          id: 'priceIncludingVat',
          header: 'Final Price (Incl. MVA)',
          enableEditing: true,
          enableSorting: false,
          Cell: ({ row }) => (
            <>
              {MvaPriceUtils.formatPrice(
                MvaPriceUtils.getAmountIncludingMva(getDisplayedPriceInfo(row))
              )}
            </>
          ),
          Edit: ({ row }) => {
            const draftValues =
              applicablePriceDraft ??
              getApplicablePriceDraft(getDisplayedPriceInfo(row));
            ensureApplicablePriceValuesOnRow(row, draftValues);

            return (
              <TextField
                fullWidth
                type='number'
                size='small'
                margin='none'
                variant='outlined'
                value={draftValues.priceIncludingVat}
                onChange={(event) =>
                  updateApplicablePriceDraft(
                    row,
                    'priceIncludingVat',
                    event.target.value
                  )
                }
              />
            );
          },
        }
      );
    }

    baseColumns.push({
      accessorKey: 'isDefault',
      header: 'Default',
      enableSorting: false,
      enableEditing: false,
      Cell: ({ renderedCellValue, row }) => {
        //Hide the cell when adding a new row
        if (row.id === 'mrt-row-create') {
          return null;
        }

        return row.original.isDefault ? (
          <IconButton color='primary'>
            <RadioButtonCheckedIcon />
          </IconButton>
        ) : (
          <Tooltip title='Set as Default'>
            <IconButton color='primary' onClick={() => handleClickSetAsDefault(row)}>
              <RadioButtonUncheckedIcon />
            </IconButton>
          </Tooltip>
        );
      },
      Edit: ({ cell, renderedCellValue }) => <></>,
    });

    return baseColumns;
  }, [
    applicablePriceDraft,
    currentApplicablePricesById,
    showApplicablePrices,
    validationErrors,
  ]);

  //call CREATE hook
  const { mutateAsync: createPortion, isPending: isCreatingPortion } =
    useCreateNewPortion();

  const {
    mutateAsync: createPortionSizePrice,
    isPending: isCreatingPortionSizePrice,
  } = useCreateNewPortionSizePrice();

  // console.log('isFetchingMealPortions', isFetchingMealPortions);
  // console.log('isLoadingMealPortions', isLoadingMealPortions);

  //call DELETE hook
  const { mutateAsync: deletePortion, isPending: isDeletingPortion } =
    useDeletePortion();

  //Call setPortion default
  const {
    mutateAsync: setPortionAsDefault,
    isPending: isSettingPortionDefault,
  } = useSetPortionAsDefault();

  //CREATE action
  const handleCreatePortion = async ({ values, table }) => {
    const initialPricePayload = buildInitialPortionPricePayload(values);
    const newValidationErrors = validatePortion(
      {
        ...values,
        priceExcludingVat: initialPricePayload.amount,
      },
      {
        requirePrice: !showApplicablePrices,
      }
    );
    if (Object.values(newValidationErrors).some((error) => error)) {
      setValidationErrors(newValidationErrors);
      return;
    }
    setValidationErrors({});
    try {
      const res = await createPortion({ mealId, portion: values });
      console.log('handleCreatePortion', res?.data);

      if (shouldCreateInitialPortionPrice(initialPricePayload)) {
        await createPortionSizePrice({
          portionSizeId: res.data.id,
          priceInfo: initialPricePayload,
        });
      }
    } catch (err) {
      console.log('createPortion', err.message);
      toast.error(err.message);
      return;
    }
    setApplicablePriceDraft(null);
    table.setCreatingRow(null); //exit creating mode
  };

  //DELETE action
  const openDeleteConfirmModal = async (row) => {
    if (window.confirm('Are you sure you want to delete this Portion?')) {
      try {
        await deletePortion({ mealId, portionId: row.original.id });
      } catch (err) {
        console.error(err);
      }
    }
  };

  const handleClickSetAsDefault = async (row) => {
    if (
      window.confirm('Are you sure you want to set this Portion as DEFAULT?')
    ) {
      try {
        await setPortionAsDefault({ mealId, portionId: row.original.id });
      } catch (err) {
        console.log(err);
      }
    }
  };

  const table = useMaterialReactTable({
    columns,
    data: fetchedMealPortions.data,
    createDisplayMode: 'row', // ('modal', and 'custom' are also available)
    enableEditing: true,
    enableColumnActions: false,
    enableGlobalFilter: false,
    positionActionsColumn: 'last',
    positionCreatingRow: 'bottom',
    enableBottomToolbar: true,
    enableTopToolbar: false,
    enableFilters: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enablePagination: false,
    initialState: {
      columnVisibility: { id: false },
      sorting: [
        {
          id: 'size',
          desc: false,
        },
      ],
    }, //hide firstName column by default

    getRowId: (row) => row.id,
    muiToolbarAlertBannerProps: isLoadingUsersError
      ? {
          color: 'error',
          children: 'Error loading data',
        }
      : undefined,

    onCreatingRowCancel: () => {
      setValidationErrors({});
      setApplicablePriceDraft(null);
    },
    onCreatingRowSave: handleCreatePortion,
    renderRowActions: ({ row, table }) => (
      <Box sx={{ display: 'flex', gap: '1rem' }}>
        <Tooltip title='Edit'>
          <PriceHistoryFormDialog mealId={mealId} portionInfo={row.original} />
        </Tooltip>
        <Tooltip title='Delete'>
          <IconButton color='error' onClick={() => openDeleteConfirmModal(row)}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </Box>
    ),

    renderBottomToolbarCustomActions: ({ table }) => (
      <Button
        variant='contained'
        onClick={() => {
          setApplicablePriceDraft(null);
          table.setCreatingRow(true);
        }}
      >
        Add row
      </Button>
    ),

    muiTableHeadCellProps: () => ({
      sx: {
        backgroundColor: '#BDBDBD80',
        borderBottom: '2px solid #342C47',
        paddingY: '1.5rem',
        color: '#342C47',
      },
    }),

    state: {
      isLoading: isLoadingMealPortions,
      isSaving:
        isCreatingPortion || isCreatingPortionSizePrice || isDeletingPortion,
      isSettingPortionDefault,
      showAlertBanner: isLoadingUsersError,
      showProgressBars:
        isFetchingMealPortions ||
        (showApplicablePrices && isFetchingApplicablePrices),
    },
  });

  return <MaterialReactTable table={table} />;
};

const validateRequired = (value) => {
  if (typeof value === 'string') {
    return value.trim().length > 0;
  } else if (typeof value === 'number') {
    return !isNaN(value);
  } else {
    return false;
  }
};

function validatePortion(portion, { requirePrice = true } = {}) {
  const numericPrice = MvaPriceUtils.getNumericValue(portion.priceExcludingVat);
  const hasPriceValidationError = requirePrice
    ? !validateRequired(portion.priceExcludingVat)
    : numericPrice !== null && numericPrice < 0;

  return {
    size: !validateRequired(portion.size) ? 'Portion size is required' : '',
    minimumQuantity: !validateRequired(portion.minimumQuantity)
      ? 'Min Quantity is Required'
      : '',
    maximumQuantity: !validateRequired(portion.maximumQuantity)
      ? 'Max Quantity is Required'
      : '',
    priceExcludingVat: hasPriceValidationError
      ? requirePrice
        ? 'Payable Price is Required'
        : 'Payable Price must be 0 or greater'
      : '',
  };
}

export default PortionFormTable;
